'use client'

import React, { useCallback, useMemo, useState } from 'react'

import { type DragOperation } from '@dnd-kit/abstract'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { Columns3, GripVertical, RotateCcw } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import PopoverV2 from '@/components/common/PopoverV2'

import type {
  ColumnDefinition,
  UseColumnPreferencesReturn,
} from '@/hooks/useColumnPreferences'

// ============================================================================
// SortableColumnItem - Individual draggable column item
// ============================================================================

interface SortableColumnItemProps {
  column: ColumnDefinition & { visible: boolean }
  index: number
  onToggle: () => void
  canHide: boolean
}

/**
 * A single sortable column item with checkbox and drag handle.
 * Uses @dnd-kit/react/sortable for drag and drop.
 */
function SortableColumnItem({
  column,
  index,
  onToggle,
  canHide,
}: SortableColumnItemProps) {
  const { ref, handleRef, isDragging } = useSortable({
    id: column.id,
    index,
  })

  return (
    <div
      ref={ref}
      className={twMerge(
        'group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
        isDragging
          ? 'bg-accent-50 dark:bg-accent-900/30'
          : 'hover:bg-primary-50 dark:hover:bg-primary-800',
      )}
    >
      {/* Drag handle */}
      <button
        ref={handleRef}
        type="button"
        className={twMerge(
          'cursor-grab touch-none rounded p-0.5 text-primary-400 transition-colors',
          'hover:text-primary-600 dark:hover:text-primary-300',
          'opacity-0 group-hover:opacity-100',
          isDragging && 'cursor-grabbing opacity-100',
        )}
        aria-label={`Drag to reorder ${column.label}`}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      {/* Checkbox */}
      <label className="flex flex-1 cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={column.visible}
          onChange={canHide ? onToggle : undefined}
          disabled={!canHide}
          className={twMerge(
            'h-3.5 w-3.5 rounded border-primary-300 text-accent-500',
            'focus:ring-1 focus:ring-accent-500 focus:ring-offset-0',
            'dark:border-primary-600 dark:bg-primary-800',
            !canHide && 'cursor-not-allowed opacity-50',
          )}
        />
        <span
          className={twMerge(
            'select-none text-sm text-primary-700 dark:text-primary-300',
            !canHide && 'text-primary-500 dark:text-primary-400',
          )}
        >
          {column.label}
        </span>
      </label>
    </div>
  )
}

// ============================================================================
// ColumnSelector Component
// ============================================================================

/**
 * Props for ColumnSelector component
 */
export interface ColumnSelectorProps {
  /** Column definitions with id and label */
  columns: ColumnDefinition[]
  /** Column preferences from useColumnPreferences hook */
  preferences: UseColumnPreferencesReturn
  /** Optional className for the trigger button */
  className?: string
  /** Alignment of popover (default: 'end') */
  align?: 'start' | 'center' | 'end'
}

/**
 * ColumnSelector - Popover for toggling column visibility and reordering
 *
 * Features:
 * - Checkbox for each column to toggle visibility
 * - Drag and drop to reorder columns
 * - Reset to defaults button
 * - Uses shared Button and PopoverV2 components
 * - Integrates with useColumnPreferences hook
 *
 * @example
 * ```tsx
 * const columnDefs = [
 *   { id: 'date', label: 'Date' },
 *   { id: 'amount', label: 'Amount' },
 * ]
 *
 * const preferences = useColumnPreferences('transactions', columnDefs)
 *
 * <ColumnSelector columns={columnDefs} preferences={preferences} />
 * ```
 */
export function ColumnSelector({
  columns,
  preferences,
  className,
  align = 'end',
}: ColumnSelectorProps) {
  const { isVisible, toggleColumn, moveColumn, resetToDefaults, isModified } =
    preferences
  const [open, setOpen] = useState(false)

  // Build ordered columns with visibility state
  const orderedColumns = useMemo(() => {
    return preferences.columns
      .map((pc) => {
        const def = columns.find((c) => c.id === pc.id)
        return def ? { ...def, visible: pc.visible } : null
      })
      .filter((c): c is ColumnDefinition & { visible: boolean } => c !== null)
  }, [preferences.columns, columns])

  // Build a map of column ID to current index for O(1) lookup
  const columnIdToIndex = useMemo(() => {
    const map: Record<string, number> = {}
    orderedColumns.forEach((col, idx) => {
      map[col.id] = idx
    })
    return map
  }, [orderedColumns])

  // Handle drag end - reorder columns
  // The sortable pattern gives us the NEW index via source.index
  const handleDragEnd = useCallback(
    (event: { operation: DragOperation }) => {
      const { source } = event.operation
      if (!source) return

      const columnId = source.id as string
      // @ts-ignore - dnd-kit types don't expose index but it's there
      const newIndex = source.index as number | undefined

      if (typeof newIndex !== 'number') return

      const currentIndex = columnIdToIndex[columnId]
      if (currentIndex === undefined || currentIndex === newIndex) return

      moveColumn(currentIndex, newIndex)
    },
    [columnIdToIndex, moveColumn],
  )

  return (
    <PopoverV2 open={open} onOpenChange={setOpen}>
      <PopoverV2.Trigger asChild>
        <Button
          variant="ghost-compact"
          className={twMerge(
            'relative gap-1.5 text-xs text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200',
            className,
          )}
        >
          <span className="relative">
            <Columns3 className="h-3.5 w-3.5" />
            {isModified && (
              <span
                className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-accent-500"
                aria-label="Columns modified from default"
              />
            )}
          </span>
          Columns
        </Button>
      </PopoverV2.Trigger>

      <PopoverV2.Content align={align} className="w-56 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-primary-200 px-3 py-2 dark:border-primary-700">
          <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
            Toggle Columns
          </span>
          <button
            type="button"
            onClick={resetToDefaults}
            className={twMerge(
              'inline-flex items-center gap-1 rounded px-1.5 py-0.5',
              'text-xs font-medium text-primary-500',
              'transition-colors hover:bg-primary-100 hover:text-primary-700',
              'dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-300',
            )}
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>

        {/* Sortable column list */}
        <div className="max-h-[300px] overflow-y-auto p-2">
          {/* @ts-ignore - dnd-kit type mismatch */}
          <DragDropProvider onDragEnd={handleDragEnd}>
            {orderedColumns.map((column, index) => (
              <SortableColumnItem
                key={column.id}
                column={column}
                index={index}
                onToggle={() => toggleColumn(column.id)}
                canHide={column.canHide !== false}
              />
            ))}
          </DragDropProvider>
        </div>

        {/* Footer hint */}
        <div className="border-t border-primary-200 px-3 py-1.5 dark:border-primary-700">
          <span className="text-xs text-primary-400 dark:text-primary-500">
            Drag to reorder columns
          </span>
        </div>
      </PopoverV2.Content>
    </PopoverV2>
  )
}

export default ColumnSelector
